<template>
  <div>
    <v-client-table :data="data.otImpressions"
      class='mgt-10px is-paddingless'
      :columns="columns"
      :options="options">
      <template #selected="{ row }">
        <b-checkbox v-model="row.selected"
          class="has-text-centered" @input="selectionOtImpression(row)">
        </b-checkbox>
      </template>
      <template #code = "{ row }">
        <section class="is-flex is-align-items-center">
          <b-icon
            v-if="getCouleurInformation(row)"
            :custom-class="getCouleurInformation(row).type"
            :class="{ 'demandeImpressionwhiteColorBorder' :
            getCouleurInformation(row).key === constantes.COULEUR_BLANC}"
            icon="checkbox-blank-circle"
            size="is-small"/>
          <router-link-blank
            :to="{ name: 'c.logistique.ordre_travail-tech', params: { id: row.id } }"
            class="lien mgl-1"
            :class="{'text-access-no-permission':
            !checkPermissions(constantes.ORDRE_TRAVAIL.lecture)}"
            :message="row.code">
          </router-link-blank>
        </section>
        <div class="mgt-10px">
          <tag-list
            v-if="getTags(row.otTagIds).length > 0"
            :listes="getTags(row.otTagIds)"/>
        </div>
      </template>
      <template #typeOtLibelle = "{ row }">
        {{ row.typeOtLibelle }}
      </template>
      <template #etat = "{ row }">
        {{ getLibelleEtat(row.etat) }}
      </template>
      <template #lieuDeStockageNomCommercial= "{ row }">
        <router-link-blank
          :to="{ name: 'c.bodac.clients/contacts-client', params: { id: row.lieuDeStockageId } }"
          class="lien"
          :class="{'text-access-no-permission':
          !checkPermissions(constantes.TIERS.lecture)}"
          :message="row.lieuDeStockageNomCommercial">
        </router-link-blank>
      </template>
      <template #quantite = "{ row }" >
        <div class="is-pulled-right">
          {{ row.quantite }}
        </div>
      </template>
      <template #graphisteEnCharge = "{ row }">
        <div class="has-text-left">
          <router-link-blank
            v-if="row.graphisteEnChargeId"
            :to="{
              name: 'c.parametrage.collaborateurs',
              params: { id: row.graphisteEnChargeId },
            }"
            class="lien"
            :message="getUser(row.graphisteEnChargeId)"/>
        </div>
      </template>
      <template #technicienEnCharge = "{ row }">
        <div class="has-text-left"
          v-if="row.technicienEnChargeId">
          <router-link-blank
            :to="{
              name: 'c.logistique.technicien',
              params: { id: row.technicienEnChargeId,
                sousTypeFrom: constantes.TIERS_SOUS_TYPE_TECHNICIEN },
            }"
            class="lien"
            :message="row.technicienEnCharge"/>
        </div>
        <qd-button v-else
          @click="checkPermissions(constantes.ORDRE_TRAVAIL.maj)
            ? $emit('prisEnCharge', row.id)
            : showPopUp()"
          :class="[
            { 'button-access-no-permission': !checkPermissions(constantes.ORDRE_TRAVAIL.maj) },
          ]"
          class="button is-primary">
          {{ $t('listeOtImpression.actions.prisEnCharge')}}
        </qd-button>
      </template>
      <template #dateDemande="{ row }">
        <section class="is-flex is-justify-content-space-between">
          {{ func.formatDate(row.dateDemande) }}
          <tag-list class="mgl-2 is-justify-content-flex-end"
            v-if="getTags(row.tagIds).length > 0"
            :listes="getTags(row.tagIds)"/>
        </section>
      </template>
      <template #materielFormatLibelles = "{ row }">
        {{ row.materielFormatLibelles.join(', ') }}
      </template>
    </v-client-table>
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
import cloneDeep from 'lodash/cloneDeep';
import formatDate from '@/services/general/formatDate';
import {
  NO_PER_PAGE_VALUE, RESULTS_PER_POP_UP, SORT_ICON, TEXTS,
} from '@/configuration/clientTableConf';
import routerLinkBlank from '@/components/general/routerLinkBlank';
import { ORDRE_TRAVAIL } from '@/configuration/logistique/permissionsLogistiqueConf';
import { LIST_ETAT_OT_TECH } from '@/configuration/logistique/ordreTravail/ordreTravailConf';
import { TIERS } from '@/configuration/permissionsBodacConf';
import { SET_OT_SELECT } from '@/store/modules/logistique/ordreTravailImpression-mutation-type';
import { PARAMETRES_KEYS } from '@/store/modules/parametres-mutation-type';
import nomPrenomGetter from '@/services/general/nomPrenomGetter';
import { COULEUR_IMPRESSION, COULEUR_BLANC } from '@/configuration/logistique/demandeImpression/demandeImpressionConf';
import tagList from '@/components/general/tagList';
import { TIERS_SOUS_TYPE_TECHNICIEN } from '@/configuration/tiers/tiersConf';

const Permissions = createNamespacedHelpers('permissions');
const OrdreTravailImpression = createNamespacedHelpers('ordreTravailImpression');
const Parametres = createNamespacedHelpers('parametres');

export default {
  name: 'childRowOtImpression',
  props: {
    data: {
      type: Object,
      required: true,
      default: null,
      constantes: {
        COULEUR_BLANC,
      },
    },
  },
  data() {
    return {
      selectAllValue: false,
      constantes: {
        ORDRE_TRAVAIL,
        TIERS,
        TIERS_SOUS_TYPE_TECHNICIEN,
      },
      func: {
        formatDate,
      },
    };
  },
  components: {
    routerLinkBlank,
    tagList,
  },
  computed: {
    ...Permissions.mapGetters({
      checkPermissions: 'checkPermissions',
    }),
    ...Parametres.mapGetters({
      parametre: 'parametre',
    }),
    columns() {
      return [
        'selected', 'code', 'typeOtLibelle', 'etat', 'lieuDeStockageNomCommercial',
        'dateDemande', 'materielFormatLibelles', 'contactDemandeur', 'graphisteEnCharge',
        'technicienEnCharge', 'quantite',
      ];
    },
    options() {
      const texts = cloneDeep(TEXTS);
      texts.count = '';
      return {
        sortIcon: SORT_ICON,
        headings: {
          code: this.$i18n.t('listeOtImpression.tabs.childrow.code'),
          typeOtLibelle: this.$i18n.t('listeOtImpression.tabs.childrow.type'),
          etat: this.$i18n.t('listeOtImpression.tabs.childrow.etat'),
          lieuDeStockageNomCommercial: this.$i18n.t('listeOtImpression.tabs.childrow.nomSite'),
          quantite: this.$i18n.t('listeOtImpression.tabs.childrow.qteAImprimer'),
          dateDemande: this.$i18n.t('listeOtImpression.tabs.childrow.dateDemande'),
          materielFormatLibelles: this.$i18n.t('listeOtImpression.tabs.childrow.materielFormatLibelles'),
          contactDemandeur: this.$i18n.t('listeOtImpression.tabs.childrow.contactDemandeur'),
          graphisteEnCharge: this.$t('listeOtImpression.tabs.childrow.graphisteEnCharge'),
          technicienEnCharge: this.$t('listeOtImpression.tabs.childrow.technicienEnCharge'),
          selected: () => {
            return '';
          },
        },
        columnsClasses: {
          selected: 'colonneSelected',
        },
        filterable: false,
        perPageValues: NO_PER_PAGE_VALUE,
        perPage: RESULTS_PER_POP_UP,
        texts: texts,
        sortable: ['code', 'typeOtLibelle', 'etat', 'lieuDeStockageNomCommercial', 'quantite'],
      };
    },
    materielTypes() {
      return this.parametre(PARAMETRES_KEYS.materielType);
    },
  },
  methods: {
    ...OrdreTravailImpression.mapMutations({
      setOtSelect: SET_OT_SELECT,
    }),
    ...Permissions.mapActions({
      showPopUp: 'showPopup',
    }),
    getLibelleEtat(etatOt) {
      const etatFind = LIST_ETAT_OT_TECH.find((etat) => {
        return etat.code === etatOt;
      });
      return etatFind ? this.$i18n.t(etatFind.libelle) : '';
    },
    selectionOtImpression(row) {
      const otId = row.id;
      this.setOtSelect({
        otId: otId,
      });
    },
    getUser(userId) {
      const userFound = this.parametre(PARAMETRES_KEYS.utilisateur)
        .find((user) => { return user.id === userId; });
      if (!userFound) return '';
      return nomPrenomGetter(userFound);
    },
    getCouleurInformation(ot) {
      let couleur = null;
      if (ot.materielTypeId) {
        couleur = this.materielTypes.find(mat => mat.id === ot.materielTypeId)?.couleur;
      }
      couleur = couleur ?? ot.couleurDemande;
      return COULEUR_IMPRESSION.find(
        (couleurImpression) => couleurImpression.key === couleur,
      );
    },
    getTags(tagIds) {
      return this.parametre(PARAMETRES_KEYS.tag).filter((tag) => {
        return tagIds.includes(tag.id);
      });
    },
  },
};
</script>

<style scoped>

</style>
